import * as THREE from 'three';
import { DysonMaterial, SegmentState, SegmentMaterialState } from '../materials/DysonMaterial';
import { HashrateMapper } from '../utils/HashrateMapper';

export interface DysonSphereConfig {
  radius: number;
  segmentCount: number;
  constructionSpeed: number;
  maxConcurrentConstruction: number;
}

export interface ConstructionFleet {
  poolName: string;
  poolHashrate: number;
  color: THREE.Color;
  drones: {
    count: number;
    positions: THREE.Vector3[];
    targetSegment: number;
  };
  workAnimation: {
    particleEffects: boolean;
    laserBeams: boolean;
    progressIndicators: boolean;
  };
}

export interface SegmentInfo {
  index: number;
  state: SegmentState;
  progress: number;
  assignedPool: string | undefined;
  position: THREE.Vector3;
  normal: THREE.Vector3;
}

export class DysonSphere extends THREE.Group {
  private config: DysonSphereConfig;
  private hashrateMapper: HashrateMapper;
  private dysonMaterial: DysonMaterial;
  
  // Sphere state
  public completionPercentage: number = 0;
  public targetPercentage: number = 0;
  public constructionSpeed: number;
  
  // Segment management
  public segments: {
    total: number;
    completed: Set<number>;
    underConstruction: Map<number, number>; // segment -> progress (0-1)
    material: DysonMaterial;
  };
  
  // Construction fleets (mining pools)
  public constructionFleets: Map<string, ConstructionFleet> = new Map();
  
  // Visual components
  private instancedMesh: THREE.InstancedMesh;
  private segmentGeometry: THREE.BufferGeometry;
  private segmentStates: SegmentMaterialState[] = [];
  
  // Animation
  private time: number = 0;
  private lastHashrateUpdate: number = 0;
  
  constructor(
    config?: Partial<DysonSphereConfig>,
    hashrateMapper?: HashrateMapper,
    dysonMaterial?: DysonMaterial
  ) {
    super();
    
    this.config = {
      radius: 20,
      segmentCount: 1000,
      constructionSpeed: 10, // segments per second
      maxConcurrentConstruction: 20,
      ...config,
    };
    
    this.hashrateMapper = hashrateMapper || new HashrateMapper();
    this.dysonMaterial = dysonMaterial || new DysonMaterial();
    this.constructionSpeed = this.config.constructionSpeed;
    
    this.segments = {
      total: this.config.segmentCount,
      completed: new Set(),
      underConstruction: new Map(),
      material: this.dysonMaterial,
    };
    
    this.createSegmentGeometry();
    this.createInstancedMesh();
    this.initializeSegmentStates();
    
    this.userData = { id: 'dyson-sphere', type: 'sphere' };
  }
  
  private createSegmentGeometry(): THREE.BufferGeometry {
    // Create hexagonal segment geometry
    this.segmentGeometry = this.createHexagonalSegment();
    return this.segmentGeometry;
  }
  
  private createHexagonalSegment(): THREE.BufferGeometry {
    // Create a hexagonal segment that will be instanced across the sphere
    const geometry = new THREE.PlaneGeometry(1, 1, 1, 1);
    
    // Modify vertices to create hexagonal shape
    const positions = geometry.attributes['position'].array as Float32Array;
    const hexRadius = 0.8;
    
    // Hexagon vertices (6 points + center)
    const hexVertices = [];
    for (let i = 0; i < 6; i++) {
      const angle = (i / 6) * Math.PI * 2;
      hexVertices.push(
        Math.cos(angle) * hexRadius,
        Math.sin(angle) * hexRadius,
        0
      );
    }
    
    // Replace plane vertices with hexagon
    const hexPositions = new Float32Array([
      // Center vertex
      0, 0, 0,
      // Hexagon vertices
      ...hexVertices,
    ]);
    
    // Create indices for triangulated hexagon
    const indices = [];
    for (let i = 0; i < 6; i++) {
      indices.push(0, i + 1, ((i + 1) % 6) + 1);
    }
    
    const hexGeometry = new THREE.BufferGeometry();
    hexGeometry.setAttribute('position', new THREE.BufferAttribute(hexPositions, 3));
    hexGeometry.setIndex(indices);
    hexGeometry.computeVertexNormals();
    
    return hexGeometry;
  }
  
  private createInstancedMesh(): void {
    const material = this.dysonMaterial.createInstancedMaterial('empty', this.config.segmentCount);
    
    this.instancedMesh = new THREE.InstancedMesh(
      this.segmentGeometry,
      material,
      this.config.segmentCount
    );
    
    // Position segments in geodesic pattern
    this.positionSegments();
    
    this.add(this.instancedMesh);
  }
  
  private positionSegments(): void {
    const matrix = new THREE.Matrix4();
    const position = new THREE.Vector3();
    const rotation = new THREE.Euler();
    const scale = new THREE.Vector3(1, 1, 1);
    
    // Use Fibonacci sphere for even distribution
    const goldenAngle = Math.PI * (3 - Math.sqrt(5)); // Golden angle in radians
    
    for (let i = 0; i < this.config.segmentCount; i++) {
      // Fibonacci sphere point distribution
      const y = 1 - (i / (this.config.segmentCount - 1)) * 2; // y goes from 1 to -1
      const radiusAtY = Math.sqrt(1 - y * y);
      const theta = goldenAngle * i;
      
      const x = Math.cos(theta) * radiusAtY;
      const z = Math.sin(theta) * radiusAtY;
      
      // Position on sphere surface
      position.set(x * this.config.radius, y * this.config.radius, z * this.config.radius);
      
      // Rotate to face outward from sphere center
      const normal = position.clone().normalize();
      rotation.setFromRotationMatrix(
        new THREE.Matrix4().lookAt(position, new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, 1, 0))
      );
      
      // Scale based on distance from equator for variation
      const scaleVariation = 0.8 + Math.abs(y) * 0.4;
      scale.setScalar(scaleVariation);
      
      matrix.compose(position, new THREE.Quaternion().setFromEuler(rotation), scale);
      this.instancedMesh.setMatrixAt(i, matrix);
      
      // Store segment info
      if (!this.segmentStates[i]) {
        this.segmentStates[i] = {
          state: 'empty',
          progress: 0,
          lastUpdate: Date.now(),
        };
      }
    }
    
    this.instancedMesh.instanceMatrix.needsUpdate = true;
  }
  
  private initializeSegmentStates(): void {
    for (let i = 0; i < this.config.segmentCount; i++) {
      this.segmentStates[i] = {
        state: 'empty',
        progress: 0,
        lastUpdate: Date.now(),
      };
    }
  }
  
  public updateHashrate(hashrateEH: number): void {
    const update = this.hashrateMapper.updateHashrate(hashrateEH);
    
    this.targetPercentage = update.metrics.completionPercentage;
    this.constructionSpeed = update.metrics.constructionSpeed;
    this.lastHashrateUpdate = Date.now();
    
    // Update current completion percentage for immediate feedback
    this.completionPercentage = update.metrics.completionPercentage;
    
    // Trigger construction toward new target
    this.updateConstruction();
  }
  
  private updateConstruction(): void {
    const targetSegments = Math.floor((this.targetPercentage / 100) * this.config.segmentCount);
    const currentCompleted = this.segments.completed.size;
    const currentBuilding = this.segments.underConstruction.size;
    
    if (targetSegments > currentCompleted + currentBuilding) {
      // Need to start more construction
      const segmentsToStart = Math.min(
        targetSegments - currentCompleted - currentBuilding,
        this.config.maxConcurrentConstruction - currentBuilding
      );
      
      for (let i = 0; i < segmentsToStart; i++) {
        const segmentIndex = this.findNextSegmentToConstruct();
        if (segmentIndex !== -1) {
          this.startSegmentConstruction(segmentIndex);
        }
      }
    }
  }
  
  private findNextSegmentToConstruct(): number {
    // Find the next empty segment, preferring those near already completed ones
    for (let i = 0; i < this.config.segmentCount; i++) {
      if (this.segmentStates[i].state === 'empty') {
        return i;
      }
    }
    return -1;
  }
  
  private startSegmentConstruction(segmentIndex: number): void {
    this.segments.underConstruction.set(segmentIndex, 0);
    this.segmentStates[segmentIndex] = {
      state: 'building',
      progress: 0,
      lastUpdate: Date.now(),
    };
    
    // Update material
    this.updateSegmentVisual(segmentIndex);
  }
  
  private completeSegmentConstruction(segmentIndex: number): void {
    this.segments.underConstruction.delete(segmentIndex);
    this.segments.completed.add(segmentIndex);
    this.segmentStates[segmentIndex] = {
      state: 'complete',
      progress: 1,
      lastUpdate: Date.now(),
    };
    
    // Update completion percentage
    this.completionPercentage = (this.segments.completed.size / this.config.segmentCount) * 100;
    
    // Update material
    this.updateSegmentVisual(segmentIndex);
  }
  
  private updateSegmentVisual(segmentIndex: number): void {
    const segmentState = this.segmentStates[segmentIndex];
    const material = this.instancedMesh.material as THREE.ShaderMaterial;
    
    this.dysonMaterial.updateInstancedMaterial(material, segmentIndex, segmentState);
  }
  
  public getSegmentState(index: number): SegmentState {
    if (this.segments.completed.has(index)) {
      return 'complete';
    } else if (this.segments.underConstruction.has(index)) {
      return 'building';
    } else {
      return 'empty';
    }
  }
  
  public assignFleetToSegment(poolName: string, segmentIndex: number): void {
    const fleet = this.constructionFleets.get(poolName);
    if (fleet && this.segmentStates[segmentIndex]) {
      fleet.drones.targetSegment = segmentIndex;
      this.segmentStates[segmentIndex].poolColor = fleet.color;
      
      // Update visual with pool color
      this.updateSegmentVisual(segmentIndex);
    }
  }
  
  public addConstructionFleet(poolName: string, poolHashrate: number, color: THREE.Color): void {
    const fleetSize = this.hashrateMapper.getFleetSize(poolHashrate);
    
    const fleet: ConstructionFleet = {
      poolName,
      poolHashrate,
      color: color.clone(),
      drones: {
        count: fleetSize,
        positions: [],
        targetSegment: -1,
      },
      workAnimation: {
        particleEffects: true,
        laserBeams: true,
        progressIndicators: true,
      },
    };
    
    // Initialize drone positions around the sphere
    this.initializeFleetPositions(fleet);
    
    this.constructionFleets.set(poolName, fleet);
  }
  
  private initializeFleetPositions(fleet: ConstructionFleet): void {
    fleet.drones.positions = [];
    
    for (let i = 0; i < fleet.drones.count; i++) {
      // Random position around the sphere at construction distance
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      const radius = this.config.radius * 1.2; // Outside the sphere
      
      const position = new THREE.Vector3(
        radius * Math.sin(phi) * Math.cos(theta),
        radius * Math.sin(phi) * Math.sin(theta),
        radius * Math.cos(phi)
      );
      
      fleet.drones.positions.push(position);
    }
  }
  
  public removeConstructionFleet(poolName: string): void {
    this.constructionFleets.delete(poolName);
  }
  
  public update(deltaTime: number): void {
    this.time += deltaTime;
    
    // Update material time
    this.dysonMaterial.update(deltaTime);
    
    // Update construction progress
    this.updateConstructionProgress(deltaTime);
    
    // Update fleet animations
    this.updateFleetAnimations(deltaTime);
  }
  
  private updateConstructionProgress(deltaTime: number): void {
    const constructionRate = this.constructionSpeed * deltaTime;
    
    for (const [segmentIndex, progress] of this.segments.underConstruction.entries()) {
      const newProgress = Math.min(progress + constructionRate, 1.0);
      
      if (newProgress >= 1.0) {
        // Segment construction completed
        this.completeSegmentConstruction(segmentIndex);
      } else {
        // Update progress
        this.segments.underConstruction.set(segmentIndex, newProgress);
        this.segmentStates[segmentIndex].progress = newProgress;
        this.updateSegmentVisual(segmentIndex);
      }
    }
    
    // Check if we need more construction
    if (this.segments.underConstruction.size < this.config.maxConcurrentConstruction) {
      this.updateConstruction();
    }
  }
  
  private updateFleetAnimations(_deltaTime: number): void {
    for (const fleet of this.constructionFleets.values()) {
      // Update drone positions to orbit around their target segments
      if (fleet.drones.targetSegment >= 0) {
        const segmentMatrix = new THREE.Matrix4();
        this.instancedMesh.getMatrixAt(fleet.drones.targetSegment, segmentMatrix);
        
        const segmentPosition = new THREE.Vector3();
        segmentMatrix.decompose(segmentPosition, new THREE.Quaternion(), new THREE.Vector3());
        
        // Animate drones around the target segment
        fleet.drones.positions.forEach((dronePosition, index) => {
          const orbitRadius = 2.0;
          const orbitSpeed = 1.0;
          const phase = (index / fleet.drones.count) * Math.PI * 2;
          const angle = this.time * orbitSpeed + phase;
          
          const offset = new THREE.Vector3(
            Math.cos(angle) * orbitRadius,
            Math.sin(angle) * orbitRadius,
            Math.sin(this.time + phase) * 0.5
          );
          
          dronePosition.copy(segmentPosition).add(offset);
        });
      }
    }
  }
  
  public getConstructionInfo(): {
    completionPercentage: number;
    segmentsCompleted: number;
    segmentsBuilding: number;
    totalSegments: number;
    fleetCount: number;
  } {
    return {
      completionPercentage: this.completionPercentage,
      segmentsCompleted: this.segments.completed.size,
      segmentsBuilding: this.segments.underConstruction.size,
      totalSegments: this.config.segmentCount,
      fleetCount: this.constructionFleets.size,
    };
  }
  
  public getSegmentInfo(index: number): SegmentInfo | null {
    if (index < 0 || index >= this.config.segmentCount) {
      return null;
    }
    
    const matrix = new THREE.Matrix4();
    this.instancedMesh.getMatrixAt(index, matrix);
    
    const position = new THREE.Vector3();
    const quaternion = new THREE.Quaternion();
    const scale = new THREE.Vector3();
    matrix.decompose(position, quaternion, scale);
    
    const normal = position.clone().normalize();
    
    return {
      index,
      state: this.segmentStates[index].state,
      progress: this.segmentStates[index].progress,
      assignedPool: this.segmentStates[index].poolColor ? 'assigned' : undefined,
      position,
      normal,
    };
  }
  
  public setQualityLevel(level: 'low' | 'medium' | 'high' | 'ultra'): void {
    // Adjust segment count and visual quality based on performance
    const qualitySettings = {
      low: { segmentCount: 200, maxConstruction: 5 },
      medium: { segmentCount: 500, maxConstruction: 10 },
      high: { segmentCount: 1000, maxConstruction: 20 },
      ultra: { segmentCount: 2000, maxConstruction: 40 },
    };
    
    const settings = qualitySettings[level];
    this.config.segmentCount = settings.segmentCount;
    this.config.maxConcurrentConstruction = settings.maxConstruction;
    
    // Update segments total
    this.segments.total = settings.segmentCount;
    
    // Clear existing segment states
    this.segments.completed.clear();
    this.segments.underConstruction.clear();
    
    // Recreate sphere with new quality settings
    this.remove(this.instancedMesh);
    this.instancedMesh.dispose();
    this.createInstancedMesh();
    this.initializeSegmentStates();
  }
  
  public dispose(): void {
    // Dispose geometry and materials
    this.segmentGeometry.dispose();
    this.instancedMesh.dispose();
    this.dysonMaterial.dispose();
    
    // Clear references
    this.segments.completed.clear();
    this.segments.underConstruction.clear();
    this.constructionFleets.clear();
  }
}